// src/pages/transferReceiveRecord/components/TransferReceiveRecordTable.js
import React, { useMemo, useState } from "react";
import { Table, Space } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { FileOutlined } from "@ant-design/icons";
import _ from "lodash";
import { formatToCompanyTimezone } from "../../../lib/functions";

const TransferReceiveRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.transferReceiveRecord
  );
  const authInfo = useSelector((state) => state.authInfoReducer);

  const isWarehouse = authInfo?.is_warehouse === "yes";
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Decide mode
  const filterMode = filters.filterType;
  const recordType = filters.recordType;

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };


  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "From Branch", dataIndex: "fromLocation" },
    { title: "To Location", dataIndex: "toLocation" },
    { title: "Transfer By", dataIndex: "createdBy" },
    {
      title: "Total",
      dataIndex: "total",
      align: "right",
    },
    {
      title: "Actions",
      width: 80,
      render: (_, record) => (
        <Space>
          <Link to={`/inventory/quick-transfer-voucher/${record.t_id || record.id}`}>
            <FileOutlined style={{ fontSize: 18 }} />
          </Link>
        </Space>
      ),
    },
  ];

  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "From Branch", dataIndex: "fromLocation" },
    { title: "To Location", dataIndex: "toLocation" },
    { title: "Item", dataIndex: "item" },
    { title: "Qty", dataIndex: "qty", align: "right" },
    {
      title: "Rate",
      dataIndex: "rate",
      align: "right",
    },
    {
      title: "Total",
      dataIndex: "total",
      align: "right",
    },
    {
      title: "Actions",
      width: 80,
      render: (_, record) => (
        <Link to={`/inventory/quick-transfer-voucher/${record.t_id || record.id}`}>
          <FileOutlined style={{ fontSize: 18 }} />
        </Link>
      ),
    },
  ];

  // Detail columns used for /get-transfer-receive-details (By Item mode)
  const detailColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "t_voucher_no" },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "To Branch", dataIndex: "to_branch_name" },
    { title: "Item Name", dataIndex: "item_name" },
    ...(authInfo?.is_serial === "yes"
      ? [
          {
            title: "Serials",
            dataIndex: "serials",
            render: (val) => {
              if (!val || val.length === 0) return "";
              // val is array of objects {serial_number}
              const seq = val.map((s) => s.serial_number).join(", ");
              return `(${seq})`;
            },
          },
        ]
      : []),
    ...(isWarehouse
      ? [
          {
            title: "Warehouse",
            dataIndex: "to_warehouse_name",
          },
        ]
      : []),
    { title: "Rate", dataIndex: "item_rate", align: "right", render: (v) => (v == null ? "-" : Number(v).toFixed(2)) },
    { title: "QTY", dataIndex: "item_qty", align: "right" },
    { title: "Total", dataIndex: "item_total", align: "right", render: (v) => (v == null ? "-" : Number(v).toFixed(2)) },
    {
      title: "Actions",
      width: 80,
      render: (_, record) => (
        <Link to={`/inventory/quick-transfer-voucher/${record.t_id || record.id}`}>
          <FileOutlined style={{ fontSize: 18 }} />
        </Link>
      ),
    },
  ];

  // Helper to render grouped (By Group/By Category) view
  const groupedSections = useMemo(() => {
    if (!["By Group", "By Category"].includes(filterMode)) return null;
    // Expect list to contain details with group_name or category_name
    const groupKey = filterMode === "By Group" ? "group_name" : "category_name";
    const grouped = _.groupBy(list, (d) => d[groupKey] || "Others");
    return grouped; // { "Group A": [rows], "Group B": [rows] }
  }, [filterMode, list]);

  // Decide which table to render
  if (["By Item", "By Group", "By Category"].includes(filterMode)) {
    // By Item -> single table of details
    if (filterMode === "By Item") {
      return (
        <Table
          rowKey="id"
          columns={detailColumns}
          dataSource={list}
          loading={loading}
          pagination={printMode ? false : { pageSize: 10 }}
          bordered
          size={printMode ? "small" : "middle"}
          scroll={printMode ? undefined : { x: "max-content" }}
        />
      );
    }

    // By Group or By Category -> render each group with a small table (like old UI)
    return (
      <div>
        {Object.keys(groupedSections || {}).map((groupName) => (
          <div key={groupName} style={{ marginBottom: 18 }}>
            <h4 style={{ margin: "8px 0" }}>
              {filterMode === "By Group" ? "Group Name: " : "Category Name: "}
              {groupName}
            </h4>
            <Table
              rowKey={(r) => `${r.t_id}-${r.item_id}`}
              columns={detailColumns}
              dataSource={groupedSections[groupName]}
              pagination={false}
              bordered
              size="small"
              scroll={{ x: "max-content" }}
            />
          </div>
        ))}
      </div>
    );
  }

  // Normal flows:
  if (recordType === "with item") {
    return (
      <Table
        rowKey="id"
        columns={withItemColumns}
        dataSource={list}
        loading={loading}
        pagination={printMode ? false : { pageSize: 10 }}
        bordered
        size={printMode ? "small" : "middle"}
        scroll={printMode ? undefined : { x: "max-content" }}
      />
    );
  }

  // summary (without item)
  return (
    <Table
      rowKey="id"
      columns={summaryColumns}
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      size={printMode ? "small" : "middle"}
      scroll={printMode ? undefined : { x: "max-content" }}
    />
  );
};

export default TransferReceiveRecordTable;
